import { ThemeProvider } from "@material-ui/core";

import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import "react-pro-sidebar/dist/css/styles.css";

import { Notification } from "./components/notification";
import { DashboardLayout } from "./layouts/dashboard";
import { MainLayout } from "./layouts/main";
import { PAGES, PrivatePage } from "./pages";
import { Home } from "./pages/home";
import { LoginPage } from "./pages/login";
import { Portfolio } from "./pages/portfolio";
import { PortfolioSetup } from "./pages/portfolio/Setup";
import { PortfolioSuccess } from "./pages/portfolio/Success";
import store, { persistor } from "./redux/store";
import { theme } from "./theme";
import "./styles/proSideBar.scss";
import { DashboardPage } from "./pages/dashboard";
import { UnrealizedPage } from "./pages/unrealized";
import { CarbonImpactPage } from "./pages/carbonImpact";
import { BuildingPage } from "./pages/building";
import { BuildingAllPage } from "./pages/buildings";
import { PowerAssetsPage } from "./pages/powerAssets";
import { PowerRevenuesPage } from "./pages/powerRevenues";
import { NOIPage } from "./pages/noi";
import { WorkspaceLayout } from "./layouts/workspace";
import { SettingsPageLayout } from "./layouts/settings";
import { ForgetPasswordPage } from "./pages/forgotPassword";
import { ResetPasswordPage } from "./pages/resetPassword";
import { PrivacyPolicyPage } from "./pages/privacyPolicy";
import { AssistancePage, TaskPage, ReportsPage } from "./pages/workspace";
import { MyProfile } from "./pages/workspace/settings/tabs/MyProfile";
import { CompanySettings } from "./pages/workspace/settings/tabs/CompanySettings";
import { PortfolioProfileTab } from "./pages/workspace/settings/tabs/portfolioProfile";
import { PortfolioRegistration } from "./pages/workspace/settings/tabs/PortfolioRegistration";
import { UsersPermission } from "./pages/workspace/settings/tabs/UsersPermission";
import { AlertNotifications } from "./pages/workspace/settings/tabs/AlertNotifications";
import { PrassIntegrations } from "./pages/workspace/settings/tabs/PrassIntegrations";
import { Support } from "./pages/workspace/settings/tabs/Support";
import { ChangePasswordPage } from "./pages/changePassword";
import { EmailSuccessPage } from "./pages/emailSuccess";
import { WelcomePage } from "./pages/aroundtown/WelcomePage";
import { PortfolioRegistrationPage } from "./pages/aroundtown/PortfolioRegistrationPage";
import { NYCMallsPortfolioRegistrationPage } from "./pages/nycMalls/PortfolioRegistrationPage";
import { NoSolarPortfolioRegistrationPage } from "./pages/demo/noSolar";
import { HotelsPortfolioRegistrationPage } from "./pages/demo/hotels";
import { DemoLayout } from "./layouts/demo";
import { CustomRegistrationPage } from "./pages/registration";
import { CustomPortfolioPage } from "./pages/registration/Portfolio";
import { ComplexPage } from "./pages/complex";
import { BuildingsLayout } from "./layouts/buildings";
import { LogoutCallback } from "./pages/login/sso/logout";

const App = () => {
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <Routes>
              <Route path={PAGES.login} element={<LoginPage />} />
              <Route path={PAGES.forgetPassword} element={<ForgetPasswordPage />} />
              <Route path={PAGES.resetPassword} element={<ResetPasswordPage />} />
              <Route
                path={PAGES.changePassword}
                element={<PrivatePage component={ChangePasswordPage} />}
              />
              <Route path={PAGES.emailSuccess} element={<EmailSuccessPage />} />
              <Route path={PAGES.privacyPolicy} element={<PrivacyPolicyPage />} />
              <Route path={PAGES.home} element={<PrivatePage component={MainLayout} />}>
                <Route index element={<Home />} />
              </Route>

              <Route
                path={PAGES.gettingStarted.index}
                element={<PrivatePage component={MainLayout} />}
              >
                <Route index element={<Portfolio />} />
                <Route path={PAGES.gettingStarted.setup} element={<PortfolioSetup />} />
                <Route path={PAGES.gettingStarted.success} element={<PortfolioSuccess />} />
              </Route>

              <Route
                path={PAGES.workspace.index}
                element={<PrivatePage component={WorkspaceLayout} />}
              >
                <Route index path={PAGES.workspace.tasks} element={<TaskPage />} />
                <Route path={PAGES.workspace.buildings} element={<TaskPage />} />

                <Route path={PAGES.workspace.assistance} element={<AssistancePage />} />
                <Route path={PAGES.workspace.reports} element={<ReportsPage />} />
                <Route
                  path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}`}
                  element={<SettingsPageLayout />}
                >
                  <Route index element={<MyProfile />} />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.companySettings}`}
                    element={<CompanySettings />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.portfolioProfile}`}
                    element={<PortfolioProfileTab />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.portfolioRegistration}`}
                    element={<PortfolioRegistration />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.usersPermission}`}
                    element={<UsersPermission />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.alertsNotifications}`}
                    element={<AlertNotifications />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.praasIntegrations}`}
                    element={<PrassIntegrations />}
                  />
                  <Route
                    path={`${PAGES.workspace.index}${PAGES.workspace.settings.index}/${PAGES.workspace.settings.support}`}
                    element={<Support />}
                  />
                </Route>
              </Route>

              <Route
                path={PAGES.dashboard.index}
                element={<PrivatePage component={DashboardLayout} />}
              >
                <Route index element={<DashboardPage />} />
                <Route path={PAGES.dashboard.unrealized} element={<UnrealizedPage />} />
                <Route path={PAGES.dashboard.carbonImpact} element={<CarbonImpactPage />} />
                <Route path={PAGES.dashboard.powerAssets} element={<PowerAssetsPage />} />
                <Route path={PAGES.dashboard.powerRevenues} element={<PowerRevenuesPage />} />
                <Route path={PAGES.dashboard.noi} element={<NOIPage />} />
              </Route>

              <Route
                path={PAGES.buildings.index}
                element={<PrivatePage component={BuildingsLayout} />}
              >
                <Route
                  path={`${PAGES.buildings.index}/:id`}
                  exact={true}
                  element={<BuildingPage />}
                />
                <Route index element={<BuildingAllPage />} />
                <Route path={`${PAGES.buildings.complexes}/:id`} element={<ComplexPage />} />
              </Route>

              {/* Routes for a special account Aroundtown */}
              <Route path={PAGES.demo.aroundtown} element={<PrivatePage component={MainLayout} />}>
                <Route index element={<WelcomePage />} />
                <Route
                  path={PAGES.demo.portfolioRegistration}
                  element={<PortfolioRegistrationPage />}
                />
              </Route>

              {/* Routes for a special account NYC Malls */}
              <Route path={PAGES.demo.solar} element={<PrivatePage component={MainLayout} />}>
                <Route index element={<WelcomePage />} />
                <Route
                  path={PAGES.demo.portfolioRegistration}
                  element={<NYCMallsPortfolioRegistrationPage />}
                />
              </Route>

              {/* Routes for default flow */}
              <Route path={PAGES.demo.default} element={<PrivatePage component={DemoLayout} />}>
                <Route index element={<WelcomePage />} />
                <Route
                  path={PAGES.demo.portfolioRegistration}
                  element={<NoSolarPortfolioRegistrationPage />}
                />
              </Route>

              {/* Routes for hotels flow*/}
              <Route path={PAGES.demo.hotels} element={<PrivatePage component={DemoLayout} />}>
                <Route index element={<WelcomePage />} />
                <Route
                  path={PAGES.demo.portfolioRegistration}
                  element={<HotelsPortfolioRegistrationPage />}
                />
              </Route>

              {/* Routes for generic registration */}
              <Route
                path={PAGES.registration.index}
                element={<PrivatePage component={DemoLayout} />}
              >
                <Route index element={<CustomRegistrationPage />} />
                <Route path={PAGES.registration.portfolio} element={<CustomPortfolioPage />} />
              </Route>

              <Route path={PAGES.ssoLogoutCallback} element={<LogoutCallback />} />
              <Route path="*" element={<LoginPage />} />
            </Routes>
            <Notification />
          </BrowserRouter>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
